import { useState } from 'react';
import { motion } from 'framer-motion';
import { KeyRound } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import type { LicenseRow, LicensesResult } from '../types/org.types';

type Tab = 'user' | 'psl' | 'feature';

function utilColor(pct: number | null): string {
  if (pct === null) return '#DAD8D1';
  if (pct >= 90) return '#D64545';
  if (pct >= 70) return '#C7851A';
  return '#3FA96B';
}

function LicenseTable({ rows, hideStatus }: { rows: LicenseRow[]; hideStatus?: boolean }) {
  if (rows.length === 0) {
    return <div className="text-sm text-neutral-500 py-6">None visible to this user.</div>;
  }
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-2">
          <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
            <th className="text-left px-4 py-3 font-medium">License</th>
            {!hideStatus && <th className="text-left px-4 py-3 font-medium">Status</th>}
            <th className="text-right px-4 py-3 font-medium">Used</th>
            <th className="text-right px-4 py-3 font-medium">Total</th>
            <th className="text-left px-4 py-3 font-medium w-56">Utilisation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
              <td className="px-4 py-2.5">
                <div className="text-crux-light">{r.label ?? r.name}</div>
                {r.label && r.name !== r.label ? (
                  <div className="text-[11px] text-neutral-600 font-mono">{r.name}</div>
                ) : null}
              </td>
              {!hideStatus && (
                <td className="px-4 py-2.5 text-neutral-400">{r.status ?? '-'}</td>
              )}
              <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                {r.usedLicenses?.toLocaleString() ?? '-'}
              </td>
              <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                {r.totalLicenses === null
                  ? '-'
                  : r.totalLicenses < 0
                    ? 'Unlimited'
                    : r.totalLicenses.toLocaleString()}
              </td>
              <td className="px-4 py-2.5">
                {r.utilPct !== null ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(100, r.utilPct)}%`, background: utilColor(r.utilPct) }}
                      />
                    </div>
                    <span className="text-xs text-neutral-400 tabular-nums w-12 text-right">
                      {r.utilPct.toFixed(0)}%
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-neutral-600">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function LicensesPage({ data }: { data?: LicensesResult | null } = {}) {
  const storeLicenses = useOrgStore((s) => s.audit.licenses);
  const scanMode = data !== undefined;
  const licenses = scanMode ? data : storeLicenses;
  const [tab, setTab] = useState<Tab>('user');

  if (!licenses)
    return <EmptyState title="Licence data not loaded" hint="Reconnect to scan again." icon={<KeyRound size={20} />} />;

  const t = licenses.totals;
  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'user', label: 'User Licenses', count: licenses.userLicenses.length },
    { key: 'psl', label: 'Permission Set Licenses', count: licenses.permissionSetLicenses.length },
    { key: 'feature', label: 'Feature Licenses', count: licenses.featureLicenses.length },
  ];
  const activeRows =
    tab === 'user'
      ? licenses.userLicenses
      : tab === 'psl'
        ? licenses.permissionSetLicenses
        : licenses.featureLicenses;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-adopt mb-1">Licenses</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">License Inventory</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Every user, permission set, and feature licence in the org with utilisation. Saturated licences
          are deployment blockers; under-utilised ones are immediate cost-recovery opportunities.
        </p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          <StatCard
            key="ul-used"
            label="User licences used"
            value={t.userLicensesUsed.toLocaleString()}
            hint={`of ${t.userLicensesTotal.toLocaleString()} total`}
            accent="adopt"
          />,
          <StatCard
            key="ul-rows"
            label="User licence types"
            value={licenses.userLicenses.length}
            accent="adopt"
          />,
          <StatCard
            key="psl-used"
            label="PSLs assigned"
            value={t.pslUsed.toLocaleString()}
            hint={`of ${t.pslTotal.toLocaleString()} total`}
            accent="adopt"
          />,
          <StatCard
            key="psl-rows"
            label="PSL types"
            value={licenses.permissionSetLicenses.length}
            accent="adopt"
          />,
        ].map((c, i) => (
          <motion.div
            key={i}
            variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
          >
            {c}
          </motion.div>
        ))}
      </motion.div>

      <div className="flex items-center gap-1">
        {tabs.map((tabInfo) => (
          <button
            key={tabInfo.key}
            type="button"
            onClick={() => setTab(tabInfo.key)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              tab === tabInfo.key
                ? 'bg-adopt/15 border-adopt/40 text-adopt'
                : 'bg-surface-2 border-border text-neutral-400 hover:text-crux-light'
            }`}
          >
            {tabInfo.label} <span className="text-neutral-500">({tabInfo.count})</span>
          </button>
        ))}
      </div>

      <LicenseTable rows={activeRows.filter((r) => !r.included)} hideStatus={tab === 'feature'} />

      {activeRows.some((r) => r.included) && (
        <details className="border border-border rounded-md">
          <summary className="cursor-pointer px-4 py-3 text-sm text-neutral-400 hover:text-crux-light">
            Included with Salesforce, no cost ({activeRows.filter((r) => r.included).length})
          </summary>
          <div className="p-3 pt-0">
            <LicenseTable rows={activeRows.filter((r) => r.included)} hideStatus={tab === 'feature'} />
          </div>
        </details>
      )}

      {licenses.notes.length > 0 && (
        <div className="text-[11px] text-neutral-600 space-y-1">
          {licenses.notes.map((n, i) => (
            <p key={i}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
